import {Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {
  BusinessCaseCheckInCancelReason,
  BusinessCaseCheckInSkipReason,
  BusinessCaseCustomerSatisfactionResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, parseDate, suffixTestId} from 'shared/web';

const SKIP_REASON_KEYS: Record<BusinessCaseCheckInSkipReason, string> = {
  NO_CONTACT_ON_ENABLED_CHANNELS:
    'page.businessCase.customerSatisfaction.skipReason.noContactOnEnabledChannels',
  CUSTOMER_REFUSED_COMMUNICATION:
    'page.businessCase.customerSatisfaction.skipReason.customerRefusedCommunication',
  ALREADY_RATED: 'page.businessCase.customerSatisfaction.skipReason.alreadyRated',
  CHECK_IN_DISABLED: 'page.businessCase.customerSatisfaction.skipReason.checkInDisabled',
  ONLINE_OFFER_UNAVAILABLE:
    'page.businessCase.customerSatisfaction.skipReason.onlineOfferUnavailable',
};
const CANCEL_REASON_KEYS: Record<BusinessCaseCheckInCancelReason, string> = {
  REOPENED: 'entity.activityPanel.activity.businessCaseReopened',
  UNSUCCESSFUL: 'page.businessCase.customerSatisfaction.cancelReason.unsuccessful',
};

export function CustomerSatisfactionEmptyState(
  props: {data: BusinessCaseCustomerSatisfactionResponseBody} & RequiredTestIdProps
) {
  const formatDateTime = useDateTimeFormatter();
  const formatDate = (date: string | null) =>
    isNotNil(date) ? formatDateTime('dateTimeMedium', parseDate(date)) : EMPTY_PLACEHOLDER;
  const message = match(props.data.feedbackState)
    .with('SENT', () =>
      i18n.t('page.businessCase.customerSatisfaction.noAnswerYet', {
        date: formatDate(props.data.checkInSentAt),
      })
    )
    .with('SCHEDULED', () =>
      i18n.t('page.businessCase.customerSatisfaction.scheduledFor', {
        date: formatDate(props.data.checkInScheduledAt),
      })
    )
    .with('SKIPPED', () =>
      i18n.t('page.businessCase.customerSatisfaction.skipped', {
        reason: isNotNil(props.data.skipReason)
          ? i18n.t(SKIP_REASON_KEYS[props.data.skipReason])
          : EMPTY_PLACEHOLDER,
      })
    )
    .with('CANCELLED', () =>
      i18n.t('page.businessCase.customerSatisfaction.notSentWithReason', {
        reason: isNotNil(props.data.cancelReason)
          ? i18n.t(CANCEL_REASON_KEYS[props.data.cancelReason])
          : EMPTY_PLACEHOLDER,
      })
    )
    .with('NOT_SENT', () =>
      props.data.checkInEnabled
        ? i18n.t('page.businessCase.customerSatisfaction.notSent')
        : i18n.t('page.businessCase.customerSatisfaction.notSentWithReason', {
            reason: i18n.t(SKIP_REASON_KEYS.CHECK_IN_DISABLED),
          })
    )
    .with('ANSWERED', () => i18n.t('page.businessCase.customerSatisfaction.answered'))
    .exhaustive();

  return <Text data-testid={suffixTestId('emptyState', props)}>{message}</Text>;
}
